// src/features/purchasing2/components/CategorySidebar.jsx
import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { Purchasing2Api } from '../api';
import { IoChevronDown, IoChevronForward } from 'react-icons/io5';

export function CategorySidebar({ selectedCategory, onCategorySelect, searchTerm }) {
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [hideEmpty, setHideEmpty] = useState(true);

  // Load parent categories
  const { data: parentCategories, isLoading: isLoadingParents } = useQuery(
    'parent-categories',
    () => Purchasing2Api.getParentCategories(),
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
    }
  );

  // Load all categories for subcategory structure
  const { data: categoriesData, isLoading: isLoadingCategories } = useQuery(
    'categories',
    () => Purchasing2Api.getCategories(),
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
    }
  );

  const toggleCategory = (categoryId) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  const handleCategoryClick = (categoryId, categoryName) => {
    onCategorySelect(categoryId, categoryName);
  };

  const handleParentCategoryClick = (parentCategoryId, parentCategoryName) => {
    // If parent category is expanded, collapse it and select it
    if (expandedCategories.has(parentCategoryId)) {
      setExpandedCategories(prev => {
        const newSet = new Set(prev);
        newSet.delete(parentCategoryId);
        return newSet;
      });
    } else {
      // Expand it first
      setExpandedCategories(prev => new Set([...prev, parentCategoryId]));
    }
    onCategorySelect(parentCategoryId, parentCategoryName);
  };

  if (isLoadingParents || isLoadingCategories) {
    return (
      <div className="w-64 bg-gray-50 border-r border-gray-200 p-4">
        <div className="animate-pulse space-y-3">
          <div className="h-4 bg-gray-300 rounded w-3/4"></div>
          <div className="h-3 bg-gray-300 rounded w-1/2"></div>
          <div className="h-3 bg-gray-300 rounded w-2/3"></div>
          <div className="h-3 bg-gray-300 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  // Group subcategories by parent
  const subcategoriesByParent = {};
  if (categoriesData?.categoriesByParent) {
    Object.keys(categoriesData.categoriesByParent).forEach(parentId => {
      subcategoriesByParent[parentId] = categoriesData.categoriesByParent[parentId];
    });
  }

  return (
    <div className="w-64 bg-gray-50 border-r border-gray-200 flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 flex-shrink-0">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
            Categories
          </h3>
          <label className="flex items-center text-xs text-gray-600">
            <input
              type="checkbox"
              checked={hideEmpty}
              onChange={(e) => setHideEmpty(e.target.checked)}
              className="mr-1 h-3 w-3 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            Hide empty
          </label>
        </div>
        {searchTerm && (
          <p className="text-xs text-gray-500 mt-1">
            Filtered by: "{searchTerm}"
          </p>
        )}
      </div>

      {/* Categories List */}
      <div className="flex-1 overflow-y-auto min-h-0">
        <div className="p-2 space-y-1">
          {/* All Items */}
          <button
            onClick={() => handleCategoryClick(null, 'All Items')}
            className={`w-full text-left px-3 py-2 text-sm rounded-md transition-colors ${
              selectedCategory === null
                ? 'bg-blue-100 text-blue-900 font-medium'
                : 'text-gray-700 hover:bg-gray-200'
            }`}
          >
            All Items
          </button>

          {/* Parent Categories */}
          {parentCategories?.filter(category => !hideEmpty || category.item_count > 0).map((parentCategory) => {
            const isExpanded = expandedCategories.has(parentCategory.category_id);
            const subcategories = (subcategoriesByParent[parentCategory.category_id] || []).filter(sub => !hideEmpty || sub.item_count > 0);
            const isSelected = selectedCategory === parentCategory.category_id;

            return (
              <div key={parentCategory.category_id} className="space-y-1">
                {/* Parent Category */}
                <div className="flex items-center">
                  <button
                    onClick={() => handleParentCategoryClick(parentCategory.category_id, parentCategory.category_name)}
                    className={`flex-1 text-left px-3 py-2 text-sm rounded-md transition-colors ${
                      isSelected
                        ? 'bg-blue-100 text-blue-900 font-medium'
                        : 'text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span>{parentCategory.category_name}</span>
                      <span className="text-xs text-gray-500">
                        {parentCategory.item_count}
                      </span>
                    </div>
                  </button>
                  
                  {subcategories.length > 0 && (
                    <button
                      onClick={() => toggleCategory(parentCategory.category_id)}
                      className="p-1 text-gray-400 hover:text-gray-600"
                    >
                      {isExpanded ? (
                        <IoChevronDown className="w-4 h-4" />
                      ) : (
                        <IoChevronForward className="w-4 h-4" />
                      )}
                    </button>
                  )}
                </div>

                {/* Subcategories */}
                {isExpanded && subcategories.length > 0 && (
                  <div className="ml-4 space-y-1">
                    {subcategories.map((subcategory) => (
                      <button
                        key={subcategory.category_id}
                        onClick={() => handleCategoryClick(subcategory.category_id, subcategory.category_name)}
                        className={`w-full text-left px-3 py-2 text-sm rounded-md transition-colors ${
                          selectedCategory === subcategory.category_id
                            ? 'bg-blue-100 text-blue-900 font-medium'
                            : 'text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span>{subcategory.category_name}</span>
                          <span className="text-xs text-gray-500">
                            {subcategory.item_count}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-gray-200 flex-shrink-0">
        <div className="text-xs text-gray-500">
          {parentCategories?.length || 0} categories
        </div>
      </div>
    </div>
  );
}
